/**
 * Small dropdown menu positioned `fixed` off its trigger button ($refs.menuButton),
 * so it cannot be clipped by overflow containers (e.g. the roster grid's
 * horizontal scroller). Flips above the button when the viewport below is too
 * short; closes on scroll/resize instead of tracking (menus are short-lived).
 * The menu element itself must carry max-h ≤ MENU_MAX_HEIGHT with overflow-y-auto.
 */
const MENU_MAX_HEIGHT = 240
// a button at least this much wider than the menu anchors the menu at the click
const CLICK_ANCHOR_RESERVE = 64

/**
 * Left edge of the menu. Narrow buttons (about the menu's width) keep the menu
 * right-aligned with the button. A wide button (full cell width, e.g. the
 * roster's "sign up") would put a right-aligned menu far away from the pointer,
 * so there the click's x becomes the menu's left edge - clamped into the
 * button and the viewport so nothing is cut off. Without pointer coordinates
 * (keyboard, Enter) the menu stays right-aligned.
 */
export function menuLeftFor({ rect, menuWidth, viewportWidth, clientX = null }) {
  const rightAligned = rect.right - menuWidth
  const withinViewport = (left) => Math.max(8, Math.min(left, viewportWidth - menuWidth - 8))

  if (clientX === null || rect.width < menuWidth + CLICK_ANCHOR_RESERVE) {
    return withinViewport(rightAligned)
  }

  return withinViewport(Math.max(rect.left, Math.min(clientX, rightAligned)))
}

export default ({ width = 256 } = {}) => ({
  menuOpen: false,
  menuLeft: 0,
  menuWidth: width,
  // exactly one of top/bottom is set (bottom-anchored when flipped above)
  menuTop: null,
  menuBottom: null,
  _closeRef: null,

  toggleMenu(event = null) {
    this.menuOpen ? this.closeMenu() : this.openMenu(event)
  },

  get menuStyle() {
    return {
      left: `${this.menuLeft}px`,
      width: `${this.menuWidth}px`,
      top: this.menuTop === null ? null : `${this.menuTop}px`,
      bottom: this.menuBottom === null ? null : `${this.menuBottom}px`
    }
  },

  openMenu(event = null) {
    const rect = this.$refs.menuButton.getBoundingClientRect()
    this.menuWidth = Math.min(width, window.innerWidth - 16)
    // a real pointer click carries clientX; keyboard activation reports 0/0
    const clientX = event && typeof event.clientX === 'number' && (event.clientX !== 0 || event.clientY !== 0)
      ? event.clientX
      : null
    this.menuLeft = menuLeftFor({ rect, menuWidth: this.menuWidth, viewportWidth: window.innerWidth, clientX })
    if (window.innerHeight - rect.bottom >= MENU_MAX_HEIGHT + 8) {
      this.menuTop = rect.bottom + 4
      this.menuBottom = null
    } else {
      this.menuTop = null
      this.menuBottom = window.innerHeight - rect.top + 4
    }
    this.menuOpen = true

    this._closeRef = () => this.closeMenu()
    window.addEventListener('scroll', this._closeRef, true)
    window.addEventListener('resize', this._closeRef)
  },

  closeMenu() {
    this.menuOpen = false
    if (this._closeRef) {
      window.removeEventListener('scroll', this._closeRef, true)
      window.removeEventListener('resize', this._closeRef)
      this._closeRef = null
    }
  },

  destroy() {
    this.closeMenu()
  }
})
